import {LocalDate} from '@js-joda/core';
import {EmployeeSchedule, Project, ProjectSchedule, ProjectScheduleWeek} from './EmployeeSchedulesCompiler';

type State = EmployeeSchedule[] | null;

type InitialLoadAction = {
    type : 'initialLoad';
    payload : {
        employeeSchedules : EmployeeSchedule[] | null;
    };
};

type UpdateLockAction = {
    type : 'updateLock';
    payload : {
        employeeId : number;
        projectId : number;
        locked : boolean;
    };
};

type RefreshAction = {
    type : 'refresh';
    payload : {
        employeeSchedule : EmployeeSchedule;
    };
};

type UpdateScheduleAction = {
    type : 'updateSchedule';
    payload : {
        employeeId : number;
        projectId : number;
        startOfWeek : string;
        hours : number;
    };
};

type FillScheduleAction = {
    type : 'fillSchedule';
    payload : {
        employeeId : number;
        projectId : number;
        weeks : ProjectScheduleWeek[];
    };
};

type AddProjectAction = {
    type : 'addProject';
    payload : {
        employeeId : number;
        project : Project;
        startDate : LocalDate;
    };
};

type RemoveProjectAction = {
    type : 'removeProject';
    payload : {
        employeeId : number;
        projectId : number;
        deletedFrom : string;
    };
};

type ApplyToAllVisibleAction = {
    type : 'applyToAllVisible';
    payload : {
        employeeIds : number[];
        project : Project;
        startDate : LocalDate;
        startOfWeek : string;
        hours : number;
    };
};

export type EmployeeSchedulesAction = InitialLoadAction
    | UpdateLockAction
    | RefreshAction
    | UpdateScheduleAction
    | FillScheduleAction
    | AddProjectAction
    | RemoveProjectAction
    | ApplyToAllVisibleAction;

export const employeeSchedulesReducer = (state : State, action : EmployeeSchedulesAction) : State => {
    if (action.type === 'initialLoad') {
        return action.payload.employeeSchedules;
    }

    if (!state) {
        return state;
    }

    switch (action.type) {
        case 'updateLock':
            return state.map(employeeSchedule => {
                if (employeeSchedule.employee.id !== action.payload.employeeId) {
                    return employeeSchedule;
                }

                return {
                    ...employeeSchedule,
                    schedules: employeeSchedule.schedules.map(schedule => {
                        if (schedule.project.id !== action.payload.projectId) {
                            return schedule;
                        }

                        return {...schedule, locked: action.payload.locked};
                    }),
                };
            });

        case 'refresh':
            return state.map(employeeSchedule => {
                if (employeeSchedule.employee.id !== action.payload.employeeSchedule.employee.id) {
                    return employeeSchedule;
                }

                return action.payload.employeeSchedule;
            });

        case 'updateSchedule':
            return state.map(employeeSchedule => {
                if (employeeSchedule.employee.id !== action.payload.employeeId) {
                    return employeeSchedule;
                }

                return {
                    ...employeeSchedule,
                    schedules: employeeSchedule.schedules.map(schedule => {
                        if (schedule.project.id !== action.payload.projectId) {
                            return schedule;
                        }

                        return {
                            ...schedule,
                            weeks: schedule.weeks.map(week => {
                                if (week.startOfWeek !== action.payload.startOfWeek) {
                                    return week;
                                }

                                return {...week, scheduled: action.payload.hours};
                            }),
                        };
                    }),
                };
            });

        case 'fillSchedule':
            return state.map(employeeSchedule => {
                if (employeeSchedule.employee.id !== action.payload.employeeId) {
                    return employeeSchedule;
                }

                return {
                    ...employeeSchedule,
                    schedules: employeeSchedule.schedules.map(schedule => {
                        if (schedule.project.id !== action.payload.projectId) {
                            return schedule;
                        }

                        return {...schedule, weeks: schedule.weeks.map(scheduleWeek => {
                            const week = action.payload.weeks.find(
                                week => week.startOfWeek === scheduleWeek.startOfWeek
                            );

                            if (!week) {
                                return scheduleWeek;
                            }

                            return {...scheduleWeek, scheduled: week.scheduled};
                        })};
                    }),
                };
            });

        case 'addProject':
            return state.map(employeeSchedule => {
                if (employeeSchedule.employee.id !== action.payload.employeeId) {
                    return employeeSchedule;
                }

                return {
                    ...employeeSchedule,
                    schedules: employeeSchedule.schedules.concat({
                        project: action.payload.project,
                        locked: false,
                        weeks: new Array(8).fill(0).map((_, weekNo) => ({
                            startOfWeek: action.payload.startDate.plusWeeks(weekNo).toString(),
                            scheduled: 0,
                        })),
                    }).sort(({project : a}, {project : b}) => a.name.localeCompare(b.name)),
                };
            });

        case 'removeProject':
            return state.map(employeeSchedule => {
                if (employeeSchedule.employee.id !== action.payload.employeeId) {
                    return employeeSchedule;
                }

                const schedule = employeeSchedule.schedules.find(
                    schedule => schedule.project.id === action.payload.projectId
                );

                if (!schedule) {
                    return employeeSchedule;
                }

                const newSchedules = employeeSchedule.schedules.filter(
                    schedule => schedule.project.id !== action.payload.projectId
                );
                const newSchedule : ProjectSchedule = {
                    ...schedule,
                    locked: false,
                    weeks: schedule.weeks.map(week => ({
                        ...week,
                        scheduled: week.startOfWeek >= action.payload.deletedFrom ? 0 : week.scheduled,
                    })),
                }

                if (newSchedule.weeks.some(week => week.scheduled + (week.actual || 0) > 0)) {
                    newSchedules.push(newSchedule);
                    newSchedules.sort(({project : a}, {project : b}) => a.name.localeCompare(b.name));
                }

                return {...employeeSchedule, schedules: newSchedules};
            });

        case 'applyToAllVisible':
            return state.map(employeeSchedule => {
                if (!action.payload.employeeIds.includes(employeeSchedule.employee.id)) {
                    return employeeSchedule;
                }

                let newSchedules : ProjectSchedule[];

                if (employeeSchedule.schedules.some(schedule => schedule.project.id === action.payload.project.id)) {
                    newSchedules = employeeSchedule.schedules.map(schedule => {
                        if (schedule.project.id !== action.payload.project.id) {
                            return schedule;
                        }

                        return {
                            ...schedule,
                            weeks: schedule.weeks.map(week => {
                                if (week.startOfWeek !== action.payload.startOfWeek) {
                                    return week;
                                }

                                return {...week, scheduled: action.payload.hours};
                            }),
                        }
                    });
                } else {
                    newSchedules = [...employeeSchedule.schedules, {
                        project: action.payload.project,
                        locked: false,
                        weeks: new Array(8).fill(0).map((_, weekNo) => {
                            const startOfWeek = action.payload.startDate.plusWeeks(weekNo).toString();
                            return {
                                startOfWeek,
                                scheduled: startOfWeek === action.payload.startOfWeek ? action.payload.hours : 0,
                            };
                        }),
                    }].sort(({project : a}, {project : b}) => a.name.localeCompare(b.name));
                }

                return {...employeeSchedule, schedules: newSchedules};
            });
    }

    return state;
};
